import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { giftState, canAllocate } from '@theme/pe-gift-rules';

class PeGiftingComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.catalog = JSON.parse(this.querySelector('[data-gift-catalog]')?.textContent || '[]');
    this.copy = JSON.parse(this.querySelector('[data-gift-translations]')?.textContent || '{}');
    this.root = this.dataset.root || '/';
    this.currency = this.dataset.currency || 'GBP';
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.currency });
    this.existing = new URLSearchParams(window.location.search).get('mode') === 'box';
    this.busy = false;
    this.initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
  }

  get bottles() { return [...this.querySelectorAll('[data-bottle-select]')]; }
  get testers() { return [...this.querySelectorAll('[data-tester-select]')]; }
  get selectedIds() { return this.bottles.map(select => select.value); }
  format(value) { return this.money.format(value / 100); }
  product(id) { return this.catalog.find(product => product.id === String(id)); }

  async cart() {
    const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.controller.signal });
    if (!response.ok) throw new Error(this.copy.error);
    return response.json();
  }

  async initialize() {
    if (!this.querySelector('[data-gift-submit]')) return;
    for (const select of this.testers) this.fillSelect(select, this.catalog.filter(product => product.available), this.copy.selectTester, false);
    if (this.existing) {
      this.querySelector('[data-mode-note]').hidden = false;
      this.querySelector('[data-gift-filter]').closest('.pe-gifting__filter').hidden = true;
      this.querySelector('[data-sprays-label]').textContent = this.copy.alreadyInBasket;
      try {
        const state = giftState(await this.cart());
        if (!state.valid || state.freeCount < 4) throw new Error(this.copy.existingError);
        this.free = state.free;
        this.rebuildChoices();
        const ids = [...state.free].flatMap(([id, quantity]) => Array(Math.min(quantity, 4)).fill(id)).slice(0, 4);
        this.bottles.forEach((select, i) => { select.value = ids[i]; });
      } catch (error) { if (error.name !== 'AbortError') this.showError(error.message); return; }
    } else this.rebuildChoices();
    for (const button of this.querySelectorAll('[data-preset]')) button.disabled = button.hasAttribute('data-unavailable');
    this.selectionChanged();
  }

  fillSelect(select, products, placeholder, includePrice = true) {
    const value = select.value;
    const options = [new Option(placeholder, '')];
    for (const product of products) {
      const option = new Option(`${product.title}${includePrice ? ` · ${this.format(product.price)}` : ''}`, product.id);
      option.disabled = !product.available;
      options.push(option);
    }
    select.replaceChildren(...options);
    if (products.some(product => product.id === value)) select.value = value;
  }

  rebuildChoices() {
    const audience = this.querySelector('[data-gift-filter]').value;
    for (const select of this.bottles) {
      const choices = this.catalog.filter(product => {
        if (this.existing) return (this.free?.get(product.id) || 0) > 0;
        return product.available && (audience === 'all' || product[audience] || product.id === select.value);
      });
      this.fillSelect(select, choices, this.copy.selectFragrance, !this.existing);
    }
  }

  filterChanged() { this.rebuildChoices(); this.selectionChanged(); }

  chooseEdit(event) {
    const ids = event.target.closest('[data-preset]')?.dataset.preset.split(',').filter(Boolean);
    if (!ids || ids.length !== 4) return;
    if (this.existing) {
      window.location.assign(`${this.root}pages/gift-sets#gift-builder`);
      return;
    }
    this.querySelector('[data-gift-filter]').value = 'all';
    this.rebuildChoices();
    this.bottles.forEach((select, i) => { select.value = ids[i]; });
    this.selectionChanged();
    const heading = this.querySelector('#gift-builder h2');
    heading?.focus({ preventScroll: true });
    this.querySelector('#gift-builder')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  selectionChanged() {
    const selected = this.selectedIds.map(id => this.product(id));
    const count = selected.filter(Boolean).length;
    this.querySelector('[data-progress]').textContent = this.copy.progress.replace('{count}', count);
    const list = this.querySelector('[data-summary-list]');
    list.replaceChildren();
    this.bottles.forEach((select, index) => {
      const product = selected[index];
      const card = select.closest('.pe-gifting__bottle');
      const image = card.querySelector('[data-bottle-preview]');
      image.hidden = !product;
      if (product) { image.src = product.image; image.alt = `${product.title}, 30ml`; }
      card.querySelector('[data-bottle-detail]').textContent = this.existing && product ? this.copy.alreadyInBasket : this.copy.size;
      const li = document.createElement('li');
      li.textContent = product ? product.title : this.copy.selection.replace('{number}', index + 1);
      list.append(li);
    });
    const subtotal = this.existing ? 0 : selected.reduce((total, product) => total + (product?.price || 0), 0);
    this.querySelector('[data-spray-total]').textContent = this.existing ? this.copy.alreadyInBasket : count ? this.format(subtotal) : '—';
    this.querySelector('[data-gift-total]').textContent = count === 4 ? this.format(subtotal + Number(this.dataset.boxPrice)) : '—';
    const button = this.querySelector('[data-gift-submit]');
    button.disabled = this.busy || this.dataset.boxAvailable !== 'true';
    button.textContent = this.busy ? this.copy.adding : this.existing ? this.copy.addBox : this.copy.addSet;
    if (!this.busy) { this.querySelector('[data-gift-success]').hidden = true; this.querySelector('[data-view-basket]').hidden = true; }
  }

  showError(message) {
    const error = this.querySelector('[data-gift-error]');
    error.textContent = message || this.copy.error;
    error.hidden = false;
  }

  async addGift(event) {
    event.preventDefault();
    if (this.busy) return;
    const form = this.querySelector('form.pe-gifting__form');
    if (!form.reportValidity()) return;
    const ids = this.selectedIds;
    const fragrances = ids.map(id => this.product(id));
    const testers = this.testers.map(select => this.product(select.value));
    if (fragrances.some(product => !product?.available) || testers.some(product => !product?.available)) { this.showError(this.copy.incomplete); return; }
    this.busy = true;
    this.querySelector('[data-gift-error]').hidden = true;
    this.querySelector('[data-gift-success]').hidden = true;
    this.selectionChanged();
    const giftId = `PE-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;
    try {
      const before = await this.cart();
      if (this.existing && !canAllocate(ids, giftState(before).free)) throw new Error(this.copy.existingError);
      const properties = {
        'Gift set': giftId,
        'Four 30ml sprays': fragrances.map(product => product.title).join(' / '),
        'Spray tester 1': testers[0].title,
        'Spray tester 2': testers[1].title,
        '_pe_gift_variants': ids.join(','),
        '_pe_gift_id': giftId
      };
      const items = this.existing ? [] : ids.map(id => ({ id, quantity: 1, properties: { 'Gift set': giftId, _pe_gift_id: giftId } }));
      items.push({ id: this.dataset.boxId, quantity: 1, properties });
      const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: this.controller.signal, body: JSON.stringify({ items }) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.copy.error);
      const after = await this.cart();
      if (!giftState(after).valid) throw new Error(this.copy.invalidCart);
      this.querySelector('[data-gift-success]').textContent = this.copy.success;
      this.querySelector('[data-gift-success]').hidden = false;
      this.querySelector('[data-view-basket]').hidden = false;
      this.dispatchEvent(new CartAddEvent(after, this.id || 'pe-gifting', { source: 'pe-gifting', itemCount: after.item_count }));
      window.location.assign(`${this.root}cart`);
    } catch (error) {
      if (error.name !== 'AbortError') this.showError(error.message);
    } finally {
      this.busy = false;
      const button = this.querySelector('[data-gift-submit]');
      button.disabled = false;
      button.textContent = this.existing ? this.copy.addBox : this.copy.addSet;
    }
  }
}

if (!customElements.get('pe-gifting-component')) customElements.define('pe-gifting-component', PeGiftingComponent);
